import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  createRelationship,
  joinRelationshipWithCode,
  getRelationship,
  type Relationship,
} from '@/lib/storage';
import { getUserProfile } from '@/lib/admin';
import {
  formatPairingCode,
  unformatPairingCode,
  validatePairingCodeFormat,
  daysUntilExpiry,
} from '@/lib/pairing';
import { Copy, Check, UserPlus, Link as LinkIcon, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { trackPartnerPairing } from '@/lib/analytics';

interface PartnerPairingProps {
  userId: string;
  userEmail: string;
  relationshipId?: string | null;
  onPaired?: (relationshipId: string) => void;
}

export function PartnerPairing({ userId, userEmail, relationshipId, onPaired }: PartnerPairingProps) {
  const [mode, setMode] = useState<'select' | 'generate' | 'join'>('select');
  const [pairingCode, setPairingCode] = useState<string>('');
  const [inputCode, setInputCode] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [relationship, setRelationship] = useState<Relationship | null>(null);
  const [error, setError] = useState<string>('');
  const [partnerName, setPartnerName] = useState<string>('Partner');

  // Load existing relationship if ID provided
  useEffect(() => {
    if (relationshipId) {
      loadRelationship(relationshipId);
    }
  }, [relationshipId]);

  // Fetch partner's name from their profile when relationship is active
  useEffect(() => {
    const fetchPartnerName = async () => {
      if (relationship && relationship.status === 'active') {
        const isPartner1 = relationship.partner1Id === userId;
        const partnerId = isPartner1 ? relationship.partner2Id : relationship.partner1Id;

        if (partnerId) {
          const partnerProfile = await getUserProfile(partnerId);
          if (partnerProfile?.name) {
            setPartnerName(partnerProfile.name);
          } else {
            // Fallback to cached name in relationship
            const cachedName = isPartner1 ? relationship.partner2Name : relationship.partner1Name;
            setPartnerName(cachedName || 'Partner');
          }
        }
      }
    };

    fetchPartnerName();
  }, [relationship, userId]);

  const loadRelationship = async (relId: string) => {
    try {
      const rel = await getRelationship(relId);
      if (rel) {
        setRelationship(rel);
        // Load the pairing code if it exists, but don't automatically show it
        if (rel.status === 'pending' && rel.partner1Id === userId) {
          setPairingCode(rel.pairingCode);
        }
      }
    } catch (error) {
      console.error('Error loading relationship:', error);
    }
  };

  const handleGenerateCode = async () => {
    // If there's already a pairing code (existing pending relationship), just show it
    if (pairingCode && relationship?.status === 'pending') {
      setMode('generate');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Fetch user's name from profile
      const userProfile = await getUserProfile(userId);
      const userName = userProfile?.name;

      const result = await createRelationship(userId, userEmail, userName);
      setPairingCode(result.pairingCode);
      setMode('generate');

      if (onPaired) {
        onPaired(result.relationshipId);
      }

      trackPartnerPairing('creator');
      toast.success('Pairing code generated!');
    } catch (error: unknown) {
      const err = error as { message?: string };
      setError(err.message || 'Failed to generate pairing code');
      toast.error('Failed to generate code');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(pairingCode);
      setCopied(true);
      toast.success('Code copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy code');
    }
  };

  const handleJoinWithCode = async () => {
    setLoading(true);
    setError('');

    const cleanCode = unformatPairingCode(inputCode);

    // Validate code format
    if (!validatePairingCodeFormat(cleanCode)) {
      setError('Please enter a valid 6-digit code');
      setLoading(false);
      return;
    }

    try {
      // Fetch user's name from profile
      const userProfile = await getUserProfile(userId);
      const userName = userProfile?.name;

      const relationshipId = await joinRelationshipWithCode(userId, userEmail, cleanCode, userName);

      if (onPaired) {
        onPaired(relationshipId);
      }

      trackPartnerPairing('joiner');
      toast.success('Successfully paired with partner!');
      await loadRelationship(relationshipId);
    } catch (error: unknown) {
      const err = error as { message?: string };
      setError(err.message || 'Failed to join with code');
      toast.error(err.message || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Always allow going back to select mode
    setMode('select');
    setInputCode('');
    setError('');
  };

  // Already paired - show status
  if (relationship && relationship.status === 'active') {
    return (
      <Card className="h-full">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <LinkIcon className="h-5 w-5 text-green-600" />
            Paired with Partner
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-center justify-center py-2">
            <p className="font-medium text-base">{partnerName}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Mode: Select action
  if (mode === 'select') {
    return (
      <Card className="h-full">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Partner Pairing</CardTitle>
          <CardDescription className="text-sm">
            Connect with your partner
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0 space-y-2">
          <Button onClick={() => setMode('generate')} className="w-full h-9" variant="default" size="sm">
            <UserPlus className="h-4 w-4 mr-2" />
            Generate Code
          </Button>
          <Button onClick={() => setMode('join')} className="w-full h-9" variant="outline" size="sm">
            <LinkIcon className="h-4 w-4 mr-2" />
            Join with Code
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Mode: Generate code
  if (mode === 'generate') {
    const daysLeft = relationship ? daysUntilExpiry(relationship.expiresAt) : 7;
    const hasExistingCode = relationship && relationship.status === 'pending' && pairingCode;

    return (
      <Card className="h-full">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-lg">
            <span>Your Pairing Code</span>
            <Button variant="ghost" size="sm" onClick={handleCancel} className="h-7 w-7 p-0">
              <X className="h-4 w-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          {pairingCode ? (
            <>
              <div className="bg-muted rounded-lg p-3 text-center">
                <p className="text-2xl font-bold font-mono tracking-wider text-primary">
                  {formatPairingCode(pairingCode)}
                </p>
              </div>

              <Button onClick={handleCopyCode} className="w-full h-9" variant="secondary" size="sm">
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Code
                  </>
                )}
              </Button>

              <div className="text-xs text-muted-foreground text-center">
                <p>Expires in {daysLeft} {daysLeft === 1 ? 'day' : 'days'}</p>
              </div>
            </>
          ) : (
            <Button onClick={handleGenerateCode} disabled={loading} className="w-full h-9" size="sm">
              {loading ? 'Generating...' : 'Generate Code'}
            </Button>
          )}

          {error && (
            <div className="flex items-start gap-2 text-xs text-destructive bg-destructive/10 p-2 rounded-lg">
              <AlertCircle className="h-3 w-3 mt-0.5" />
              <p>{error}</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Mode: Join with code
  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-lg">
          <span>Join Partner</span>
          <Button variant="ghost" size="sm" onClick={handleCancel} className="h-7 w-7 p-0">
            <X className="h-4 w-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <div className="space-y-2">
          <label htmlFor="pairing-code" className="text-sm font-medium">
            Pairing Code
          </label>
          <Input
            id="pairing-code"
            type="text"
            placeholder="123 456"
            value={inputCode}
            onChange={(e) => {
              const value = e.target.value.replace(/[^\d\s]/g, '');
              setInputCode(value);
              setError('');
            }}
            maxLength={7}
            className="text-center text-xl font-mono tracking-wider h-10"
            disabled={loading}
          />
        </div>

        <Button onClick={handleJoinWithCode} disabled={loading || inputCode.length < 6} className="w-full h-9" size="sm">
          {loading ? 'Connecting...' : 'Join Partnership'}
        </Button>

        {error && (
          <div className="flex items-start gap-2 text-xs text-destructive bg-destructive/10 p-2 rounded-lg">
            <AlertCircle className="h-3 w-3 mt-0.5" />
            <p>{error}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
